import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import { Refresh, TrendingUp } from '@mui/icons-material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { apiService } from '../services/api';
import { YandexStatisticsData, VkStatisticsData } from '../types';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface StatisticsChartsProps {
  campaignId: number;
  hasYandexCampaign?: boolean;
  hasVkCampaign?: boolean;
}

const StatisticsCharts: React.FC<StatisticsChartsProps> = ({
  campaignId,
  hasYandexCampaign = false,
  hasVkCampaign = false
}) => {
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  });
  
  const [dateTo, setDateTo] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  const [yandexData, setYandexData] = useState<YandexStatisticsData[]>([]);
  const [vkData, setVkData] = useState<VkStatisticsData[]>([]);
  const [yandexLoading, setYandexLoading] = useState(false);
  const [vkLoading, setVkLoading] = useState(false);
  const [yandexError, setYandexError] = useState<string>('');
  const [vkError, setVkError] = useState<string>('');

  const loadYandexStatistics = useCallback(async () => {
    if (!hasYandexCampaign) return;
    
    setYandexLoading(true);
    setYandexError('');
    
    try {
      const result = await apiService.getYandexStatistics(campaignId, dateFrom, dateTo);
      console.log('Yandex statistics result:', result);
      
      if (result.success) {
        // Обрабатываем разные форматы ответа
        let dataArray: YandexStatisticsData[] = [];
        
        if (result.data) {
          if (Array.isArray(result.data)) {
            dataArray = result.data;
          } else if (typeof result.data === 'object' && result.data !== null) {
            // Проверяем наличие свойства result
            const dataObj = result.data as any;
            if (dataObj.result && Array.isArray(dataObj.result)) {
              dataArray = dataObj.result;
            } else {
              // Если data - объект, пробуем найти массив внутри
              const possibleArrays = Object.values(dataObj).filter(Array.isArray);
              if (possibleArrays.length > 0) {
                dataArray = possibleArrays[0] as YandexStatisticsData[];
              }
            }
          }
        }
        
        setYandexData(Array.isArray(dataArray) ? dataArray : []);
      } else {
        setYandexError(result.error || 'Ошибка загрузки данных Яндекс.Директ');
      }
    } catch (error: any) {
      console.error('Yandex statistics error:', error);
      setYandexError(error.message || 'Ошибка загрузки данных Яндекс.Директ');
    } finally {
      setYandexLoading(false);
    }
  }, [hasYandexCampaign, campaignId, dateFrom, dateTo]);

  const loadVkStatistics = useCallback(async () => {
    if (!hasVkCampaign) return;
    
    setVkLoading(true);
    setVkError('');
    
    try {
      const result = await apiService.getVkStatistics(campaignId, dateFrom, dateTo);
      console.log('VK statistics result:', result);
      
      if (result.success) {
        // Обрабатываем разные форматы ответа
        let dataArray: VkStatisticsData[] = [];
        
        if (result.data) {
          if (Array.isArray(result.data)) {
            dataArray = result.data;
          } else if (typeof result.data === 'object' && result.data !== null) {
            // Проверяем наличие свойства result
            const dataObj = result.data as any;
            if (dataObj.result && Array.isArray(dataObj.result)) {
              dataArray = dataObj.result;
            } else {
              // Если data - объект, пробуем найти массив внутри
              const possibleArrays = Object.values(dataObj).filter(Array.isArray);
              if (possibleArrays.length > 0) {
                dataArray = possibleArrays[0] as VkStatisticsData[];
              }
            }
          }
        }
        
        setVkData(Array.isArray(dataArray) ? dataArray : []);
      } else {
        setVkError(result.error || 'Ошибка загрузки данных VK Ads');
      }
    } catch (error: any) {
      console.error('VK statistics error:', error);
      setVkError(error.message || 'Ошибка загрузки данных VK Ads');
    } finally {
      setVkLoading(false);
    }
  }, [hasVkCampaign, campaignId, dateFrom, dateTo]);

  const loadAllStatistics = useCallback(() => {
    if (new Date(dateFrom) > new Date(dateTo)) {
      alert('Дата начала не может быть больше даты окончания');
      return;
    }
    
    loadYandexStatistics();
    loadVkStatistics();
  }, [dateFrom, dateTo, loadYandexStatistics, loadVkStatistics]);

  // Автоматическая загрузка статистики при монтировании компонента
  useEffect(() => {
    if (hasYandexCampaign || hasVkCampaign) {
      loadYandexStatistics();
      loadVkStatistics();
    }
  }, [hasYandexCampaign, hasVkCampaign]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prepare Yandex chart data
  const getYandexChartData = () => {
    if (Array.isArray(yandexData) && yandexData.length > 0) {
      return {
        labels: yandexData.map(item => new Date(item.date).toLocaleDateString('ru-RU')),
        datasets: [
          {
            label: 'Расходы (₽)',
            data: yandexData.map(item => item.cost || 0),
            borderColor: '#ff6b35',
            backgroundColor: 'rgba(255, 107, 53, 0.1)',
            fill: true,
            tension: 0.4,
            pointBackgroundColor: '#ff6b35',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            pointRadius: 4,
          }
        ]
      };
    }
    
    // Создаем график с нулевыми данными для выбранного периода
    const generateDateRange = () => {
      const dates = [];
      const start = new Date(dateFrom);
      const end = new Date(dateTo);
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(new Date(d).toLocaleDateString('ru-RU'));
      }
      return dates;
    };
    
    const dateLabels = generateDateRange();
    return {
      labels: dateLabels,
      datasets: [
        {
          label: 'Расходы (₽)',
          data: new Array(dateLabels.length).fill(0),
          borderColor: '#ff6b35',
          backgroundColor: 'rgba(255, 107, 53, 0.1)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#ff6b35',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
        }
      ]
    };
  };

  const yandexChartData = getYandexChartData();

  // Prepare VK chart data
  const getVkChartData = () => {
    if (Array.isArray(vkData) && vkData.length > 0) {
      return {
        labels: vkData.map(item => new Date(item.date).toLocaleDateString('ru-RU')),
        datasets: [
          {
            label: 'Расходы (₽)',
            data: vkData.map(item => item.spent || 0),
            borderColor: '#4680c2',
            backgroundColor: 'rgba(70, 128, 194, 0.1)',
            fill: true,
            tension: 0.4,
            pointBackgroundColor: '#4680c2',
            pointBorderColor: '#ffffff',
            pointBorderWidth: 2,
            pointRadius: 4,
          }
        ]
      };
    }
    
    // Создаем график с нулевыми данными для выбранного периода
    const generateDateRange = () => {
      const dates = [];
      const start = new Date(dateFrom);
      const end = new Date(dateTo);
      
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(new Date(d).toLocaleDateString('ru-RU'));
      }
      return dates;
    };
    
    const dateLabels = generateDateRange();
    return {
      labels: dateLabels,
      datasets: [
        {
          label: 'Расходы (₽)',
          data: new Array(dateLabels.length).fill(0),
          borderColor: '#4680c2',
          backgroundColor: 'rgba(70, 128, 194, 0.1)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#4680c2',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 4,
        }
      ]
    };
  };

  const vkChartData = getVkChartData();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: function(context: any) {
            return `${context.parsed.y.toLocaleString('ru-RU')} ₽`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          color: '#666666',
          font: {
            size: 12
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)'
        },
        ticks: {
          color: '#666666',
          font: {
            size: 12
          },
          callback: function(value: any) {
            return value.toLocaleString('ru-RU') + ' ₽';
          }
        }
      }
    }
  };

  if (!hasYandexCampaign && !hasVkCampaign) {
    return null;
  }

  return (
    <Card elevation={0} sx={{ mb: 3, bgcolor: '#ffffff', border: '1px solid #ececfc' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TrendingUp sx={{ color: '#667eea', mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#667eea' }}>
            Статистика расходов
          </Typography>
        </Box>

        {/* Date controls */}
        <Box sx={{ 
          display: 'flex',
          gap: 1,
          alignItems: 'end',
          flexWrap: 'wrap',
          mb: 2
        }}>
          <TextField
            label="От"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 130 }}
          />
          <TextField
            label="До"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 130 }}
          />
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={loadAllStatistics}
            disabled={yandexLoading || vkLoading}
            size="small"
            sx={{
              bgcolor: '#667eea',
              '&:hover': { bgcolor: '#5a67d8' }
            }}
          >
            Обновить
          </Button>
        </Box>

        {/* Charts */}
        <Grid container spacing={3}>
          {hasYandexCampaign && (
            <Grid item xs={12} md={hasVkCampaign ? 6 : 12}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  <Chip
                    icon={<Box component="span" sx={{ fontSize: '1.2rem' }}>📢</Box>}
                    label="Яндекс.Директ"
                    sx={{
                      bgcolor: '#fff3e0',
                      color: '#f57c00',
                      fontWeight: 600,
                      '& .MuiChip-icon': { color: '#f57c00' }
                    }}
                  />
                </Box>
                
                <Box sx={{ height: 250, position: 'relative' }}>
                  {yandexLoading ? (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      flexDirection: 'column',
                      gap: 1
                    }}>
                      <CircularProgress size={24} sx={{ color: '#ff6b35' }} />
                      <Typography variant="body2" color="text.secondary">
                        Загрузка данных...
                      </Typography>
                    </Box>
                  ) : yandexError ? (
                    <Alert severity="error" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                      {yandexError}
                    </Alert>
                  ) : (
                    <Line data={yandexChartData} options={chartOptions} />
                  )}
                </Box>
              </Box>
            </Grid>
          )}

          {hasVkCampaign && (
            <Grid item xs={12} md={hasYandexCampaign ? 6 : 12}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                  <Chip
                    icon={<Box component="span" sx={{ fontSize: '1.2rem' }}>🔵</Box>}
                    label="VK Ads"
                    sx={{
                      bgcolor: '#e8f4fd',
                      color: '#1976d2',
                      fontWeight: 600,
                      '& .MuiChip-icon': { color: '#1976d2' }
                    }}
                  />
                </Box>
                
                <Box sx={{ height: 250, position: 'relative' }}>
                  {vkLoading ? (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      flexDirection: 'column',
                      gap: 1
                    }}>
                      <CircularProgress size={24} sx={{ color: '#4680c2' }} />
                      <Typography variant="body2" color="text.secondary">
                        Загрузка данных...
                      </Typography>
                    </Box>
                  ) : vkError ? (
                    <Alert severity="error" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                      {vkError}
                    </Alert>
                  ) : (
                    <Line data={vkChartData} options={chartOptions} />
                  )}
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default StatisticsCharts;
